/**
 * Onboarding Queue Dashboard
 * Overview of all onboardings with metrics, filtering, and quick actions
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useToast,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdSearch,
  MdPeople,
  MdTimer,
  MdCheckCircle,
  MdWarning,
  MdMoreVert,
  MdVisibility,
  MdAssignment,
  MdStar,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  getOnboardings,
  getOnboardingStats,
  assignOnboarding,
} from '../../../services/onboardingManagement';
import { getLeads } from '../../../services/leadManagement';
import type { Onboarding, OnboardingStats, OnboardingStatus } from '../../../types/onboarding';
import type { Lead } from '../../../types/lead';
import { useAuth } from '../../../contexts/AuthContext';

export function OnboardingQueue() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [onboardings, setOnboardings] = useState<Onboarding[]>([]);
  const [filteredOnboardings, setFilteredOnboardings] = useState<Onboarding[]>([]);
  const [stats, setStats] = useState<OnboardingStats | null>(null);
  const [closedWonLeads, setClosedWonLeads] = useState<Lead[]>([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OnboardingStatus | 'all'>('all');
  const [specialistFilter, setSpecialistFilter] = useState<string>('all');
  
  // Create onboarding modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [targetDate, setTargetDate] = useState('');

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);
      const [onboardingsData, statsData, leadsData] = await Promise.all([
        getOnboardings(),
        getOnboardingStats(),
        getLeads(),
      ]);

      setOnboardings(onboardingsData.onboardings);
      setFilteredOnboardings(onboardingsData.onboardings);
      setStats(statsData);
      
      // Filter for closed-won leads without onboarding
      const existingLeadIds = new Set(onboardingsData.onboardings.map(o => o.leadId));
      const closedWon = leadsData.leads.filter(
        lead => lead.status === 'closed_won' && !existingLeadIds.has(lead.id)
      );
      setClosedWonLeads(closedWon);
    } catch (error) {
      toast({
        title: 'Error loading data',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...onboardings];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.customerName.toLowerCase().includes(query) ||
          o.companyName.toLowerCase().includes(query) ||
          o.customerEmail.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((o) => o.status === statusFilter);
    }

    // Specialist filter
    if (specialistFilter !== 'all') {
      if (specialistFilter === 'unassigned') {
        filtered = filtered.filter((o) => !o.assignedTo);
      } else {
        filtered = filtered.filter((o) => o.assignedTo === specialistFilter);
      }
    }

    setFilteredOnboardings(filtered);
  }, [searchQuery, statusFilter, specialistFilter, onboardings]);

  // Get unique specialists
  const specialists = Array.from(
    new Set(
      onboardings
        .filter((o) => o.assignedTo)
        .map((o) => JSON.stringify({ id: o.assignedTo, name: o.assignedToName }))
    )
  ).map((s) => JSON.parse(s));

  // Handle create onboarding
  const handleCreateOnboarding = async () => {
    if (!selectedLead || !targetDate || !user) return;

    try {
      const target = new Date(targetDate);
      
      // Import createOnboarding since we'll need it
      const { createOnboarding } = await import('../../../services/onboardingManagement');
      
      await createOnboarding(
        selectedLead.id,
        selectedLead.id, // Using leadId as customerId for now
        selectedLead.contactInfo.name,
        selectedLead.contactInfo.email,
        selectedLead.contactInfo.company || 'No company',
        selectedLead.businessInfo.doorCount,
        selectedLead.estimatedValue,
        target,
        user.uid,
        user.displayName || 'System'
      );

      toast({
        title: 'Onboarding created',
        description: `Onboarding created for ${selectedLead.contactInfo.name}`,
        status: 'success',
        duration: 3000,
      });

      onClose();
      setSelectedLead(null);
      setTargetDate('');
      loadData();
    } catch (error) {
      toast({
        title: 'Error creating onboarding',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Get status color
  const getStatusColor = (status: OnboardingStatus): string => {
    const colors: Record<OnboardingStatus, string> = {
      not_started: 'gray',
      in_progress: 'blue',
      blocked: 'red',
      completed: 'green',
      cancelled: 'orange',
    };
    return colors[status];
  };

  // Get status label
  const getStatusLabel = (status: OnboardingStatus): string => {
    const labels: Record<OnboardingStatus, string> = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      blocked: 'Blocked',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status];
  };

  // Calculate days in progress
  const getDaysInProgress = (onboarding: Onboarding): number => {
    if (!onboarding.kickoffDate) return 0;
    
    const endDate = onboarding.completedDate ? onboarding.completedDate.toDate() : new Date();
    const startDate = onboarding.kickoffDate.toDate();
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Check if overdue
  const isOverdue = (onboarding: Onboarding): boolean => {
    if (onboarding.status === 'completed' || onboarding.status === 'cancelled') return false;
    const now = new Date();
    const target = onboarding.targetCompletionDate.toDate();
    return now > target;
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Onboarding Queue</Heading>
        <HStack spacing={4}>
          <Button
            leftIcon={<Icon as={MdAdd} />}
            colorScheme="blue"
            onClick={onOpen}
            isDisabled={closedWonLeads.length === 0}
          >
            Create Onboarding
          </Button>
        </HStack>
      </Flex>

      {/* Key Metrics */}
      {stats && (
        <Grid templateColumns="repeat(5, 1fr)" gap={6} mb={6}>
          {/* Active Onboardings */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Active Onboardings</StatLabel>
                <StatNumber>{stats.inProgress}</StatNumber>
                <StatHelpText>
                  <HStack spacing={1}>
                    <Icon as={MdPeople} />
                    <Text>{stats.total} total</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Average Time-to-Value */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Avg Time-to-Value</StatLabel>
                <StatNumber>
                  {stats.averageTimeToValue > 0 
                    ? `${Math.round(stats.averageTimeToValue)} days`
                    : 'N/A'}
                </StatNumber>
                <StatHelpText>
                  <HStack spacing={1}>
                    <Icon 
                      as={MdTimer} 
                      color={stats.averageTimeToValue <= 21 ? 'green.500' : 'orange.500'}
                    />
                    <Text>Target: &lt;21 days</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* On-Time Completion */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>On-Time Rate</StatLabel>
                <StatNumber>
                  {stats.completed > 0 
                    ? `${Math.round(stats.onTimeCompletionRate)}%`
                    : 'N/A'}
                </StatNumber>
                <StatHelpText>
                  <HStack spacing={1}>
                    <Icon 
                      as={MdCheckCircle} 
                      color={stats.onTimeCompletionRate >= 80 ? 'green.500' : 'orange.500'}
                    />
                    <Text>Target: &gt;80%</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Average NPS */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Average NPS</StatLabel>
                <StatNumber>
                  {stats.averageNPS > 0 
                    ? `${Math.round(stats.averageNPS)}/10`
                    : 'N/A'}
                </StatNumber>
                <StatHelpText>
                  <HStack spacing={1}>
                    <Icon 
                      as={MdStar} 
                      color={stats.averageNPS >= 9 ? 'green.500' : stats.averageNPS >= 7 ? 'yellow.500' : 'red.500'}
                    />
                    <Text>{stats.npsPromoters} promoters</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Active Blockers */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Active Blockers</StatLabel>
                <StatNumber color={stats.activeBlockers > 0 ? 'red.500' : 'gray.500'}>
                  {stats.activeBlockers}
                </StatNumber>
                <StatHelpText>
                  <HStack spacing={1}>
                    <Icon as={MdWarning} />
                    <Text>{stats.blockersBySeverity.critical} critical</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {/* Filters */}
      <Card mb={6}>
        <CardBody>
          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
            <InputGroup>
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by name, company, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OnboardingStatus | 'all')}
            >
              <option value="all">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>

            <Select
              value={specialistFilter}
              onChange={(e) => setSpecialistFilter(e.target.value)}
            >
              <option value="all">All Specialists</option>
              <option value="unassigned">Unassigned</option>
              {specialists.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Grid>
        </CardBody>
      </Card>

      {/* Onboarding Table */}
      <Card>
        <CardHeader>
          <Heading size="md">
            Onboardings ({filteredOnboardings.length})
          </Heading>
        </CardHeader>
        <CardBody p={0}>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Customer</Th>
                  <Th>Company</Th>
                  <Th>Doors</Th>
                  <Th>Current Step</Th>
                  <Th>Progress</Th>
                  <Th>Specialist</Th>
                  <Th>Target Date</Th>
                  <Th>Status</Th>
                  <Th>Days</Th>
                  <Th>Blockers</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredOnboardings.length === 0 ? (
                  <Tr>
                    <Td colSpan={11} textAlign="center" py={8}>
                      <Text color="gray.500">No onboardings found</Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredOnboardings.map((onboarding) => {
                    const activeBlockers = onboarding.blockers.filter(b => !b.resolvedAt).length;
                    const daysInProgress = getDaysInProgress(onboarding);
                    const overdue = isOverdue(onboarding);

                    return (
                      <Tr key={onboarding.id}>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{onboarding.customerName}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {onboarding.customerEmail}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>{onboarding.companyName}</Td>
                        <Td>{onboarding.doorCount}</Td>
                        <Td>
                          <Badge colorScheme="blue" fontSize="xs">
                            {onboarding.currentStep.replace('_', ' ')}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="bold">
                              {onboarding.progress}%
                            </Text>
                            <Progress
                              value={onboarding.progress}
                              size="sm"
                              colorScheme={onboarding.progress === 100 ? 'green' : 'blue'}
                              w="100px"
                            />
                          </VStack>
                        </Td>
                        <Td>
                          {onboarding.assignedToName || (
                            <Text color="gray.400" fontSize="sm">
                              Unassigned
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Text color={overdue ? 'red.500' : 'inherit'}>
                            {formatDate(onboarding.targetCompletionDate)}
                          </Text>
                          {overdue && (
                            <Badge colorScheme="red" fontSize="xs">
                              Overdue
                            </Badge>
                          )}
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(onboarding.status)}>
                            {getStatusLabel(onboarding.status)}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{daysInProgress}</Text>
                        </Td>
                        <Td>
                          {activeBlockers > 0 ? (
                            <Badge colorScheme="red">{activeBlockers}</Badge>
                          ) : (
                            <Text color="gray.400" fontSize="sm">
                              -
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<MdMoreVert />}
                              variant="ghost"
                              size="sm"
                              aria-label="Actions"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<MdVisibility />}
                                onClick={() => navigate(`/admin/onboarding/${onboarding.id}`)}
                              >
                                View Details
                              </MenuItem>
                              {!onboarding.assignedTo && user && (
                                <MenuItem
                                  icon={<MdAssignment />}
                                  onClick={async () => {
                                    try {
                                      await assignOnboarding(
                                        onboarding.id,
                                        user.uid,
                                        user.displayName || 'User',
                                        user.uid,
                                        user.displayName || 'User'
                                      );
                                      toast({
                                        title: 'Assigned to you',
                                        status: 'success',
                                        duration: 2000,
                                      });
                                      loadData();
                                    } catch (error) {
                                      toast({
                                        title: 'Error assigning',
                                        status: 'error',
                                        duration: 3000,
                                      });
                                    }
                                  }}
                                >
                                  Assign to Me
                                </MenuItem>
                              )}
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      {/* Create Onboarding Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Onboarding</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Select Closed-Won Lead</FormLabel>
                <Select
                  placeholder="Choose a lead..."
                  onChange={(e) => {
                    const lead = closedWonLeads.find(l => l.id === e.target.value);
                    setSelectedLead(lead || null);
                  }}
                >
                  {closedWonLeads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.contactInfo.name} - {lead.contactInfo.company} ({lead.businessInfo.doorCount} doors)
                    </option>
                  ))}
                </Select>
              </FormControl>

              {selectedLead && (
                <Card variant="outline">
                  <CardBody>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm">
                        <strong>Customer:</strong> {selectedLead.contactInfo.name}
                      </Text>
                      <Text fontSize="sm">
                        <strong>Email:</strong> {selectedLead.contactInfo.email}
                      </Text>
                      <Text fontSize="sm">
                        <strong>Company:</strong> {selectedLead.contactInfo.company || 'N/A'}
                      </Text>
                      <Text fontSize="sm">
                        <strong>Doors:</strong> {selectedLead.businessInfo.doorCount}
                      </Text>
                      <Text fontSize="sm">
                        <strong>Contract Value:</strong> ${selectedLead.estimatedValue.toLocaleString()} CAD
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              )}

              <FormControl isRequired>
                <FormLabel>Target Completion Date</FormLabel>
                <Input
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateOnboarding}
              isDisabled={!selectedLead || !targetDate}
            >
              Create Onboarding
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
